import CryptoJS from 'crypto-js';

/**
 * Secure Storage Utility for Frontend Parameter Passing
 * Provides encryption/decryption for sensitive data transfer between pages
 * Uses localStorage instead of sessionStorage for persistence across tabs/windows
 * Token expiration set to 60 minutes (1 hour)
 */

class SecureStorage {
  constructor() {
    // Hardcoded encryption key - 32 characters random string
    this.encryptionKey = 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6';
    this.storagePrefix = 'ss_'; // secure storage prefix
  }

  /**
   * Encrypt and store data in sessionStorage
   * @param {string} key - Storage key
   * @param {any} data - Data to encrypt and store
   * @returns {string} - Session token for retrieval
   */
  storeSecurely(key, data) {
    try {
      // Generate unique session token
      const sessionToken = this.generateSessionToken();
      const storageKey = this.storagePrefix + sessionToken;
      
      // Add timestamp for expiration
      const dataWithTimestamp = {
        data,
        timestamp: Date.now(),
        expires: Date.now() + (20 * 60 * 1000) // 20 minutes expiration
      };
      
      // Encrypt data
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(dataWithTimestamp),
        this.encryptionKey
      ).toString();
      
      // Store in localStorage instead of sessionStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(storageKey, encrypted);
      }
      
      return sessionToken;
    } catch (error) {
      console.error('SecureStorage: Failed to store data', error);
      throw new Error('Failed to store secure data');
    }
  }

  /**
   * Retrieve and decrypt data from sessionStorage
   * @param {string} sessionToken - Session token
   * @returns {any} - Decrypted data or null if not found/expired
   */
  retrieveSecurely(sessionToken) {
    try {
      if (typeof window === 'undefined') {
        return null;
      }
      
      const storageKey = this.storagePrefix + sessionToken;
      const encrypted = localStorage.getItem(storageKey);
      
      if (!encrypted) {
        return null;
      }
      
      // Decrypt data
      const decryptedBytes = CryptoJS.AES.decrypt(encrypted, this.encryptionKey);
      const decryptedData = JSON.parse(decryptedBytes.toString(CryptoJS.enc.Utf8));
      
      // Check expiration
      if (Date.now() > decryptedData.expires) {
        this.removeSecurely(sessionToken);
        return null;
      }
      
      return decryptedData.data;
    } catch (error) {
      console.error('SecureStorage: Failed to retrieve data', error);
      return null;
    }
  }

  /**
   * Remove data from sessionStorage
   * @param {string} sessionToken - Session token
   */
  removeSecurely(sessionToken) {
    try {
      if (typeof window !== 'undefined') {
        const storageKey = this.storagePrefix + sessionToken;
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.error('SecureStorage: Failed to remove data', error);
    }
  }

  /**
   * Generate a unique session token
   * @returns {string} - Unique session token
   */
  generateSessionToken() {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substring(2);
    const combined = timestamp + random;
    
    // Hash the combined string for additional security
    return CryptoJS.SHA256(combined).toString().substring(0, 32);
  }

  /**
   * Clean up expired entries from sessionStorage
   */
  cleanupExpired() {
    try {
      if (typeof window === 'undefined') {
        return;
      }
      
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(this.storagePrefix)) {
          try {
            const encrypted = localStorage.getItem(key);
            const decryptedBytes = CryptoJS.AES.decrypt(encrypted, this.encryptionKey);
            const decryptedData = JSON.parse(decryptedBytes.toString(CryptoJS.enc.Utf8));
            
            if (now > decryptedData.expires) {
              localStorage.removeItem(key);
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('SecureStorage: Failed to cleanup expired entries', error);
    }
  }

  /**
   * Validate session token format
   * @param {string} token - Token to validate
   * @returns {boolean} - True if valid format
   */
  isValidToken(token) {
    return typeof token === 'string' && token.length === 32 && /^[a-f0-9]+$/.test(token);
  }
}

// Create singleton instance
const secureStorage = new SecureStorage();

// Auto cleanup on page load and browser events
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    secureStorage.cleanupExpired();
  });
  
  // Cleanup when tab/browser is closed
  window.addEventListener('beforeunload', () => {
    secureStorage.cleanupExpired();
  });
  
  // Cleanup when tab becomes hidden (switch tab, minimize)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      secureStorage.cleanupExpired();
    }
  });
  
  // Auto cleanup every 5 minutes
  setInterval(() => {
    secureStorage.cleanupExpired();
  }, 5 * 60 * 1000);
}

export default secureStorage;